import React, { FC, memo, useMemo } from 'react'
import { PriceWrapper, Cents } from './styles'
import { useLocale, useTranslation } from '../../../hooks/locale'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'

interface FormattedPriceProps {
    priceInBaseUnits?: number
    currency?: string
    fallbackPrice?: string
    isExpose?: boolean
}

const CENTS_IN_EURO = 100

const FormattedPrice: FC<FormattedPriceProps> = ({ priceInBaseUnits, currency, fallbackPrice, isExpose = false }) => {
    const { locale } = useLocale()
    const { t } = useTranslation()
    const { isProvidedVariantActive: isSuperscriptPriceActive, isLoading } = useABVariant(
        AB_KEY.FORMATTED_PRICE,
        AB_VARIANT_VERSION.ONE,
    )

    const parts = useMemo(() => {
        if (priceInBaseUnits === undefined || priceInBaseUnits === null || !currency) {
            return null
        }
        return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).formatToParts(
            priceInBaseUnits / CENTS_IN_EURO,
        )
    }, [priceInBaseUnits, currency, locale])

    const prefix = useMemo(() => {
        if (!fallbackPrice) {
            return ''
        }
        const fromText = t('from')
        return fallbackPrice.startsWith(fromText) ? `${fromText} ` : ''
    }, [fallbackPrice, t])

    if (isLoading) {
        return null
    }

    if (!isSuperscriptPriceActive || !parts) {
        return <>{fallbackPrice ?? null}</>
    }

    return (
        <PriceWrapper>
            {prefix}
            {parts.map((part, i) =>
                part.type === 'decimal' ? null : part.type === 'fraction' ? (
                    <Cents $isExpose={isExpose} key={i}>
                        {part.value}
                    </Cents>
                ) : (
                    part.value
                ),
            )}
        </PriceWrapper>
    )
}

export default memo(FormattedPrice)
